/**
 * RoomDataPanel - Panel for collecting Manual J room data for load calculations
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select, NumberInput } from '../ui/Input';
import WindowEditor from './WindowEditor';
import WallEditor from './WallEditor';
import {
  ROOM_TYPES,
  CONDITIONING_OPTIONS,
  CEILING_TYPES,
  FLOOR_TYPES,
  FLOOR_INSULATION_OPTIONS,
  DOOR_TYPES,
  DIRECTIONS,
  DEFAULT_WINDOW,
  DEFAULT_EXTERIOR_WALL,
  DEFAULT_DOOR,
} from '../../constants/manualJOptions';

function generateId() {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * Calculate polygon area using Shoelace formula
 * Vertices should be in world coordinates (pixels where 40px = 1 foot)
 * Returns area in square feet
 */
function calculatePolygonArea(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  area = Math.abs(area) / 2;
  // Convert from square pixels to square feet (40 pixels = 1 foot)
  return Math.round(area / (40 * 40));
}

/**
 * Get compass direction from angle (in degrees)
 */
function getCompassDirection(angleDeg) {
  // Normalize to 0-360
  const normalized = ((angleDeg % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return 'E';
  if (normalized >= 22.5 && normalized < 67.5) return 'NE';
  if (normalized >= 67.5 && normalized < 112.5) return 'N';
  if (normalized >= 112.5 && normalized < 157.5) return 'NW';
  if (normalized >= 157.5 && normalized < 202.5) return 'W';
  if (normalized >= 202.5 && normalized < 247.5) return 'SW';
  if (normalized >= 247.5 && normalized < 292.5) return 'S';
  return 'SE';
}

/**
 * Check if a room edge corresponds to an exterior wall
 * Returns the wall if found, null otherwise
 */
function findExteriorWallForEdge(p1, p2, allWalls, roomWallIds) {
  if (!allWalls || allWalls.length === 0) return null;

  const tolerance = 20; // Increased tolerance for better matching

  for (const wall of allWalls) {
    if (!wall.start || !wall.end) continue;

    // Check if this wall is one of the room's walls
    if (roomWallIds && roomWallIds.length > 0 && !roomWallIds.includes(wall.id)) continue;

    // Check if wall endpoints match the room edge (approximately)
    const matchesForward =
      Math.abs(wall.start.x - p1.x) < tolerance &&
      Math.abs(wall.start.y - p1.y) < tolerance &&
      Math.abs(wall.end.x - p2.x) < tolerance &&
      Math.abs(wall.end.y - p2.y) < tolerance;

    const matchesReverse =
      Math.abs(wall.start.x - p2.x) < tolerance &&
      Math.abs(wall.start.y - p2.y) < tolerance &&
      Math.abs(wall.end.x - p1.x) < tolerance &&
      Math.abs(wall.end.y - p1.y) < tolerance;

    if (matchesForward || matchesReverse) {
      // Treat wall as exterior unless explicitly marked as interior
      // Most walls default to exterior in residential construction
      if (wall.type !== 'interior') {
        return wall;
      }
    }
  }
  return null;
}

/**
 * Detect exterior walls from room points
 * Room uses 'points' array (not vertices), and wallIds to reference walls
 */
function detectExteriorWalls(room, allWalls) {
  // Room can have either 'points' or 'vertices' depending on how it was created
  const points = room?.points || room?.vertices;
  if (!points || points.length < 3) return [];

  const exteriorWalls = [];

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];

    // Calculate wall length in feet (40 pixels = 1 foot)
    const lengthPixels = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
    const lengthFeet = Math.round(lengthPixels / 40 * 10) / 10;

    // Skip very short walls
    if (lengthFeet < 1) continue;

    // Find if this edge matches an exterior wall
    const matchedWall = findExteriorWallForEdge(p1, p2, allWalls, room.wallIds);

    if (matchedWall) {
      // Calculate wall facing direction (perpendicular to wall, facing outward)
      const wallAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
      // Outward normal (assume room points are clockwise for standard rooms)
      const facingAngle = wallAngle + 90;
      const facing = getCompassDirection(facingAngle);

      exteriorWalls.push({
        id: generateId(),
        length: lengthFeet,
        construction: 'wood-2x4',
        insulation: 'R-13',
        facing: facing,
        isAutoDetected: true,
        wallId: matchedWall.id,
        startPoint: { x: p1.x, y: p1.y },
        endPoint: { x: p2.x, y: p2.y },
      });
    }
  }

  return exteriorWalls;
}

/**
 * Find windows on walls that belong to this room
 * Windows have wallId and position (0-1 along wall)
 */
function findWindowsForRoom(room, allWindows, allWalls) {
  if (!allWindows || allWindows.length === 0 || !room?.wallIds) return [];

  const detectedWindows = [];

  for (const win of allWindows) {
    // Check if this window is on one of the room's walls
    if (!room.wallIds.includes(win.wallId)) continue;

    // Find the wall to get its orientation
    const wall = allWalls?.find(w => w.id === win.wallId);
    if (!wall) continue;

    // Calculate facing direction from wall angle
    const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * 180 / Math.PI;
    const facing = getCompassDirection(wallAngle + 90);

    detectedWindows.push({
      id: generateId(),
      width: win.width || 36,
      height: win.height || 48,
      type: 'double-low-e',
      frame: 'wood-vinyl',
      facing: facing,
      shading: 'none',
      isAutoDetected: true,
      sourceWindowId: win.id,
    });
  }

  return detectedWindows;
}

/**
 * Find exterior doors on walls that belong to this room
 * Doors have wallId and position (0-1 along wall)
 * Only include doors on exterior walls
 */
function findDoorsForRoom(room, allDoors, allWalls) {
  if (!allDoors || allDoors.length === 0 || !room?.wallIds) return [];

  const detectedDoors = [];

  for (const door of allDoors) {
    // Check if this door is on one of the room's walls
    if (!room.wallIds.includes(door.wallId)) continue;

    // Find the wall to check if it's exterior
    const wall = allWalls?.find(w => w.id === door.wallId);
    if (!wall) continue;

    // Only include doors on exterior walls
    if (wall.type !== 'exterior') continue;

    // Calculate facing direction from wall angle
    const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * 180 / Math.PI;
    const facing = getCompassDirection(wallAngle + 90);

    // Map door type
    let doorType = 'solid-wood';
    if (door.type === 'sliding') doorType = 'sliding-glass';
    else if (door.type === 'french') doorType = 'french';
    else if (door.type === 'double') doorType = 'solid-window';

    detectedDoors.push({
      id: generateId(),
      type: doorType,
      width: door.width || 36,
      height: door.height || 80,
      facing: facing,
      isAutoDetected: true,
      sourceDoorId: door.id,
    });
  }

  return detectedDoors;
}

export default function RoomDataPanel({
  room,
  roomData,
  onUpdateRoomData,
  onClose,
  activeFloor, // Pass floor data to detect exterior walls
  isMobile = false,
}) {
  const [expandedSection, setExpandedSection] = useState('basic');
  const [hasAutoDetected, setHasAutoDetected] = useState(false);

  // Calculate floor area from room points (or vertices)
  const calculatedArea = useMemo(() => {
    const points = room?.points || room?.vertices;
    return calculatePolygonArea(points);
  }, [room?.points, room?.vertices]);

  // Initialize room data if it doesn't exist
  const data = roomData || {
    roomId: room?.id,
    name: room?.name || 'bedroom',
    conditioned: 'yes',
    floorArea: calculatedArea || room?.area || 0,
    ceilingHeight: 8,
    ceilingType: 'below-vented-attic',
    windows: [],
    exteriorWalls: [],
    floorType: 'slab',
    floorInsulation: 'none',
    doors: [],
    notes: '',
  };

  // Auto-detect room data on first open
  useEffect(() => {
    if (hasAutoDetected || !room || !activeFloor) return;

    const updates = {};
    let needsUpdate = false;

    // Auto-calculate floor area if not set
    if ((!data.floorArea || data.floorArea === 0) && calculatedArea > 0) {
      updates.floorArea = calculatedArea;
      needsUpdate = true;
    }

    // Auto-detect ceiling height from walls (wall.height is in inches)
    if (data.ceilingHeight === 8 && activeFloor.walls && activeFloor.walls.length > 0) {
      // Get the most common wall height (in case there are different heights)
      const wallHeights = activeFloor.walls
        .map(w => w.height || 96)
        .filter(h => h > 0);
      if (wallHeights.length > 0) {
        // Use the first wall's height (or could use average/max)
        const heightInches = wallHeights[0];
        const heightFeet = heightInches / 12;
        if (heightFeet !== 8) {
          updates.ceilingHeight = Math.round(heightFeet * 10) / 10; // Round to 1 decimal
          needsUpdate = true;
        }
      }
    }

    // Auto-detect exterior walls if none exist
    if (data.exteriorWalls.length === 0 && activeFloor.walls) {
      const detectedWalls = detectExteriorWalls(room, activeFloor.walls);
      if (detectedWalls.length > 0) {
        updates.exteriorWalls = detectedWalls;
        needsUpdate = true;
      }
    }

    // Auto-detect windows on room's walls
    if (data.windows.length === 0 && activeFloor.windows && activeFloor.walls) {
      const detectedWindows = findWindowsForRoom(room, activeFloor.windows, activeFloor.walls);
      if (detectedWindows.length > 0) {
        updates.windows = detectedWindows;
        needsUpdate = true;
      }
    }

    // Auto-detect exterior doors on room's walls
    if (data.doors.length === 0 && activeFloor.doors && activeFloor.walls) {
      const detectedDoors = findDoorsForRoom(room, activeFloor.doors, activeFloor.walls);
      if (detectedDoors.length > 0) {
        updates.doors = detectedDoors;
        needsUpdate = true;
      }
    }

    // Use room name if available
    if (room.name && room.name !== data.name) {
      // Try to match room name to ROOM_TYPES
      const matchedType = ROOM_TYPES.find(t =>
        room.name.toLowerCase().includes(t.value.replace('-', ' '))
      );
      if (matchedType) {
        updates.name = matchedType.value;
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      onUpdateRoomData({ ...data, ...updates, roomId: room.id });
    }

    setHasAutoDetected(true);
  }, [room, activeFloor, hasAutoDetected, data, calculatedArea, onUpdateRoomData]);

  const handleUpdate = useCallback((updates) => {
    onUpdateRoomData({ ...data, ...updates, roomId: room?.id });
  }, [data, room?.id, onUpdateRoomData]);

  // Re-run auto-detection (clears existing auto-detected items and re-detects)
  const handleRefreshAutoDetect = useCallback(() => {
    if (!room || !activeFloor) return;

    const updates = {
      floorArea: calculatedArea || 0,
    };

    // Re-detect exterior walls
    const detectedWalls = detectExteriorWalls(room, activeFloor.walls || []);
    // Keep manually added walls, replace auto-detected ones
    const manualWalls = data.exteriorWalls.filter(w => !w.isAutoDetected);
    updates.exteriorWalls = [...manualWalls, ...detectedWalls];

    // Re-detect windows
    const detectedWindows = findWindowsForRoom(room, activeFloor.windows || [], activeFloor.walls || []);
    const manualWindows = data.windows.filter(w => !w.isAutoDetected);
    updates.windows = [...manualWindows, ...detectedWindows];

    // Re-detect doors
    const detectedDoors = findDoorsForRoom(room, activeFloor.doors || [], activeFloor.walls || []);
    const manualDoors = data.doors.filter(d => !d.isAutoDetected);
    updates.doors = [...manualDoors, ...detectedDoors];

    onUpdateRoomData({ ...data, ...updates, roomId: room.id });
  }, [room, activeFloor, data, calculatedArea, onUpdateRoomData]);

  // Window handlers
  const addWindow = useCallback(() => {
    handleUpdate({
      windows: [...data.windows, { id: generateId(), ...DEFAULT_WINDOW }],
    });
  }, [data.windows, handleUpdate]);

  const updateWindow = useCallback((index, windowData) => {
    const newWindows = [...data.windows];
    newWindows[index] = windowData;
    handleUpdate({ windows: newWindows });
  }, [data.windows, handleUpdate]);

  const deleteWindow = useCallback((index) => {
    handleUpdate({
      windows: data.windows.filter((_, i) => i !== index),
    });
  }, [data.windows, handleUpdate]);

  // Wall handlers
  const addWall = useCallback(() => {
    handleUpdate({
      exteriorWalls: [...data.exteriorWalls, { id: generateId(), ...DEFAULT_EXTERIOR_WALL }],
    });
  }, [data.exteriorWalls, handleUpdate]);

  const updateWall = useCallback((index, wallData) => {
    const newWalls = [...data.exteriorWalls];
    newWalls[index] = wallData;
    handleUpdate({ exteriorWalls: newWalls });
  }, [data.exteriorWalls, handleUpdate]);

  const deleteWall = useCallback((index) => {
    handleUpdate({
      exteriorWalls: data.exteriorWalls.filter((_, i) => i !== index),
    });
  }, [data.exteriorWalls, handleUpdate]);

  // Door handlers
  const addDoor = useCallback(() => {
    handleUpdate({
      doors: [...data.doors, { id: generateId(), ...DEFAULT_DOOR }],
    });
  }, [data.doors, handleUpdate]);

  const updateDoor = useCallback((index, doorData) => {
    const newDoors = [...data.doors];
    newDoors[index] = doorData;
    handleUpdate({ doors: newDoors });
  }, [data.doors, handleUpdate]);

  const deleteDoor = useCallback((index) => {
    handleUpdate({
      doors: data.doors.filter((_, i) => i !== index),
    });
  }, [data.doors, handleUpdate]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <FloatingPanel
      title="Room Data - Manual J"
      onClose={onClose}
      position={isMobile ? 'bottom' : 'right'}
      width={isMobile ? '100%' : 320}
    >
      {/* Auto-detect header */}
      <div style={styles.autoDetectHeader}>
        <span style={styles.autoDetectLabel}>
          Auto-detects area, walls, windows & doors from floor plan
        </span>
        <Button
          variant="outline"
          size="small"
          onClick={handleRefreshAutoDetect}
          style={styles.refreshBtn}
        >
          Refresh
        </Button>
      </div>

      {/* Basic Room Info */}
      <PanelSection
        title="Basic Info"
        collapsible
        expanded={expandedSection === 'basic'}
        onToggle={() => toggleSection('basic')}
      >
        <div style={styles.field}>
          <Select
            label="Room Name"
            value={data.name}
            onChange={(name) => handleUpdate({ name })}
            options={ROOM_TYPES}
          />
        </div>

        <div style={styles.field}>
          <Select
            label="Conditioned"
            value={data.conditioned}
            onChange={(conditioned) => handleUpdate({ conditioned })}
            options={CONDITIONING_OPTIONS}
          />
        </div>

        <div style={styles.row}>
          <NumberInput
            label="Floor Area (sq ft)"
            value={data.floorArea}
            onChange={(floorArea) => handleUpdate({ floorArea: parseFloat(floorArea) || 0 })}
            min={0}
            max={10000}
            style={styles.halfWidth}
          />
          <NumberInput
            label="Ceiling Height (ft)"
            value={data.ceilingHeight}
            onChange={(ceilingHeight) => handleUpdate({ ceilingHeight: parseFloat(ceilingHeight) || 8 })}
            min={6}
            max={30}
            step={0.5}
            style={styles.halfWidth}
          />
        </div>

        <div style={styles.field}>
          <Select
            label="Ceiling Type"
            value={data.ceilingType}
            onChange={(ceilingType) => handleUpdate({ ceilingType })}
            options={CEILING_TYPES}
          />
        </div>
      </PanelSection>

      {/* Windows Section */}
      <PanelSection
        title={`Windows (${data.windows.length})`}
        collapsible
        expanded={expandedSection === 'windows'}
        onToggle={() => toggleSection('windows')}
      >
        {data.windows.map((window, index) => (
          <WindowEditor
            key={window.id}
            window={window}
            index={index}
            onUpdate={(w) => updateWindow(index, w)}
            onDelete={() => deleteWindow(index)}
          />
        ))}
        <Button
          variant="outline"
          size="small"
          onClick={addWindow}
          style={styles.addBtn}
        >
          + Add Window
        </Button>
      </PanelSection>

      {/* Exterior Walls Section */}
      <PanelSection
        title={`Exterior Walls (${data.exteriorWalls.length})`}
        collapsible
        expanded={expandedSection === 'walls'}
        onToggle={() => toggleSection('walls')}
      >
        {data.exteriorWalls.map((wall, index) => (
          <WallEditor
            key={wall.id}
            wall={wall}
            index={index}
            onUpdate={(w) => updateWall(index, w)}
            onDelete={() => deleteWall(index)}
            isAutoDetected={wall.isAutoDetected}
          />
        ))}
        <Button
          variant="outline"
          size="small"
          onClick={addWall}
          style={styles.addBtn}
        >
          + Add Exterior Wall
        </Button>
      </PanelSection>

      {/* Floor Section */}
      <PanelSection
        title="Floor"
        collapsible
        expanded={expandedSection === 'floor'}
        onToggle={() => toggleSection('floor')}
      >
        <div style={styles.field}>
          <Select
            label="Floor Type"
            value={data.floorType}
            onChange={(floorType) => handleUpdate({ floorType })}
            options={FLOOR_TYPES}
          />
        </div>
        {data.floorType !== 'slab' && data.floorType !== 'conditioned-space' && (
          <div style={styles.field}>
            <Select
              label="Floor Insulation"
              value={data.floorInsulation}
              onChange={(floorInsulation) => handleUpdate({ floorInsulation })}
              options={FLOOR_INSULATION_OPTIONS}
            />
          </div>
        )}
      </PanelSection>

      {/* Doors Section */}
      <PanelSection
        title={`Exterior Doors (${data.doors.length})`}
        collapsible
        expanded={expandedSection === 'doors'}
        onToggle={() => toggleSection('doors')}
      >
        {data.doors.map((door, index) => (
          <DoorEditor
            key={door.id}
            door={door}
            index={index}
            onUpdate={(d) => updateDoor(index, d)}
            onDelete={() => deleteDoor(index)}
          />
        ))}
        <Button
          variant="outline"
          size="small"
          onClick={addDoor}
          style={styles.addBtn}
        >
          + Add Exterior Door
        </Button>
      </PanelSection>

      {/* Notes Section */}
      <PanelSection
        title="Notes"
        collapsible
        expanded={expandedSection === 'notes'}
        onToggle={() => toggleSection('notes')}
      >
        <textarea
          value={data.notes || ''}
          onChange={(e) => handleUpdate({ notes: e.target.value })}
          placeholder="Contractor notes about this room..."
          style={styles.textarea}
        />
      </PanelSection>
    </FloatingPanel>
  );
}

// Door Editor sub-component
function DoorEditor({ door, onUpdate, onDelete, index }) {
  return (
    <div style={styles.doorContainer}>
      <div style={styles.doorHeader}>
        <span style={styles.doorTitle}>Door {index + 1}</span>
        <Button
          variant="ghost"
          size="small"
          onClick={onDelete}
          style={styles.deleteBtn}
        >
          Delete
        </Button>
      </div>

      <div style={styles.row}>
        <Select
          label="Type"
          value={door.type}
          onChange={(type) => onUpdate({ ...door, type })}
          options={DOOR_TYPES}
          style={styles.flex1}
        />
      </div>

      <div style={styles.row}>
        <NumberInput
          label="Width (in)"
          value={door.width}
          onChange={(width) => onUpdate({ ...door, width: parseInt(width) || 36 })}
          min={24}
          max={96}
          style={styles.flex1}
        />
        <NumberInput
          label="Height (in)"
          value={door.height}
          onChange={(height) => onUpdate({ ...door, height: parseInt(height) || 80 })}
          min={72}
          max={120}
          style={styles.flex1}
        />
      </div>

      <div style={styles.row}>
        <Select
          label="Facing"
          value={door.facing}
          onChange={(facing) => onUpdate({ ...door, facing })}
          options={DIRECTIONS}
          style={styles.directionSelect}
        />
      </div>
    </div>
  );
}

const styles = {
  autoDetectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: 'rgba(76, 175, 80, 0.1)',
    borderBottom: '1px solid rgba(76, 175, 80, 0.2)',
    marginBottom: '8px',
  },
  autoDetectLabel: {
    fontSize: '11px',
    color: '#81c784',
    flex: 1,
  },
  refreshBtn: {
    fontSize: '11px',
    padding: '4px 10px',
  },
  field: {
    marginBottom: '10px',
  },
  row: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
  },
  halfWidth: {
    flex: 1,
  },
  flex1: {
    flex: 1,
  },
  addBtn: {
    width: '100%',
    marginTop: '6px',
  },
  textarea: {
    width: '100%',
    minHeight: '80px',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    fontSize: '13px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  doorContainer: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  doorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  doorTitle: {
    fontWeight: 'bold',
    fontSize: '12px',
    color: '#e0e0e0',
  },
  deleteBtn: {
    color: '#ff6b6b',
    fontSize: '11px',
    padding: '2px 8px',
  },
  directionSelect: {
    width: '80px',
  },
};
